import { defineStore } from "pinia";
import type { ILink } from "@frontiers-vue3/public-pages";
import type { IArticle, IAuthor, IMagazineImpactMetrics } from "~/types";
import {
  findEditorial,
  getCoverType,
  getMagazineArticlesForGrid,
  type MagazineCoverType
} from "~/helpers/magazineHelpers";
import { MAGAZINE_PAGE_SIZE } from "~/helpers/magazineLayout";
import { appendAuthorPage } from "~/helpers/authorsPagination";
import type { IListResponse } from "~/types/IListResponse";

interface IMagazineStoreState {
  articles: IArticle[];
  impactMetrics: IMagazineImpactMetrics | null;
  coverType: MagazineCoverType;
  coverImageUrl: string | undefined;
  page: number;
  hasNextPage: boolean;
  isFetchingMore: boolean;
  breadcrumbs: ILink[];
  authors: IAuthor[];
  authorsTotal: number;
  authorsResearchTopicId: number | null;
  isLoadingAuthors: boolean;
}

const isCompletePage = (length: number): boolean =>
  length > 0 && length % MAGAZINE_PAGE_SIZE === 0;

const crumb = (text: string, url = ""): ILink => ({ text, url });

export const useMagazineStore = defineStore("magazine", {
  state: (): IMagazineStoreState => ({
    articles: [],
    impactMetrics: null,
    coverType: "thumb",
    coverImageUrl: undefined,
    page: 1,
    hasNextPage: false,
    isFetchingMore: false,
    breadcrumbs: [],
    authors: [],
    authorsTotal: 0,
    authorsResearchTopicId: null,
    isLoadingAuthors: false
  }),
  getters: {
    hasMagazineData: (state) =>
      state.impactMetrics !== null || state.articles.length > 0,
    /** Prefer store value after fetch; fall back to RT until then. */
    resolvedCoverType(): MagazineCoverType {
      const main = useMain();
      return this.hasMagazineData
        ? this.coverType
        : getCoverType(main.currentResearchTopic);
    },
    editorial: (state) => findEditorial(state.articles),
    articlesForGrid: (state) => getMagazineArticlesForGrid(state.articles)
  },
  actions: {
    async fetchInitialMagazineData(
      researchTopicId: number,
      initialPage = 1
    ) {
      const main = useMain();
      const {
        getMagazineArticles,
        getMagazineImpactMetrics,
        getCoverType: resolveCoverType,
        getCoverImageUrl
      } = useMagazineService();

      const page = Math.max(1, Math.floor(initialPage) || 1);
      const articles = await getMagazineArticles(
        researchTopicId,
        0,
        page * MAGAZINE_PAGE_SIZE
      );

      this.articles = articles;
      this.impactMetrics = getMagazineImpactMetrics(main.currentResearchTopic);
      this.coverType = resolveCoverType(main.currentResearchTopic);
      this.coverImageUrl = getCoverImageUrl(main.currentResearchTopic);
      this.page = page;
      this.hasNextPage = isCompletePage(articles.length);
      this.isFetchingMore = false;
      this.updateBreadcrumbs();
    },

    async fetchMoreArticles(researchTopicId: number) {
      if (this.isFetchingMore || !this.hasNextPage) {
        return [];
      }

      this.isFetchingMore = true;
      try {
        const { getMagazineArticles } = useMagazineService();
        const more = await getMagazineArticles(
          researchTopicId,
          this.articles.length,
          MAGAZINE_PAGE_SIZE
        );

        this.articles = [...this.articles, ...more];
        this.hasNextPage = isCompletePage(more.length);
        this.page = Math.max(
          1,
          Math.ceil(this.articles.length / MAGAZINE_PAGE_SIZE)
        );
        return more;
      } finally {
        this.isFetchingMore = false;
      }
    },

    updateBreadcrumbs() {
      const main = useMain();
      const journal = main.getPrimaryJournal;
      const section =
        journal?.sections?.find((s) => s.isPrimary) ?? journal?.sections?.[0];

      this.breadcrumbs = [
        crumb("Frontiers home", "/"),
        ...(journal
          ? [
              crumb(
                journal.title,
                journal.isOnline ? `/journals/${journal.slug}` : ""
              )
            ]
          : []),
        ...(section
          ? [
              crumb(
                section.title,
                section.isOnline
                  ? `/journals/${journal!.slug}/sections/${section.slug}`
                  : ""
              )
            ]
          : []),
        crumb("Research Topics", "/research-topics")
      ];
    },

    appendAuthors(researchTopicId: number, page: IListResponse<IAuthor>) {
      const next = appendAuthorPage(
        {
          researchTopicId: this.authorsResearchTopicId,
          authors: this.authors,
          total: this.authorsTotal
        },
        researchTopicId,
        page
      );
      this.authorsResearchTopicId = next.researchTopicId;
      this.authors = next.authors;
      this.authorsTotal = next.total;
    }
  }
});
